import React, { useContext, useEffect, useRef, useState } from "react";
import firebase from "../auth/firebase";
import { completeEmailChangeAction } from "../auth/emailChange";
import {
  clearPendingEmailChange,
  EMAIL_CHANGE_LINK_INTENT,
  EMAIL_CHANGE_MODES,
  getEmailChangeLinkIntent,
  getPendingEmailChange,
} from "../auth/pendingEmailChange";
import { AuthContext } from "../context/AuthContext";
import useGraphql from "../Hooks/useGraphql";
import Loader from "./Loader";
import { toast } from "sonner";

export const getFirebaseAction = (search = window.location.search) => {
  const params = new URLSearchParams(search);
  const mode = params.get("mode");
  if (!params.get("oobCode") || !["signIn", "verifyAndChangeEmail"].includes(mode)) return null;
  const action = { mode, oobCode: params.get("oobCode") };
  const intent = getEmailChangeLinkIntent(search);
  return intent ? { ...action, intent } : action;
};

export const stripFirebaseActionParams = () => {
  const url = new URL(window.location.href);
  ["mode", "oobCode", "apiKey", "continueUrl", "lang", "tenantId"].forEach((key) => url.searchParams.delete(key));
  window.history.replaceState(null, "", `${url.pathname}${url.search}${url.hash}`);
};

const FirebaseActionLinkHandler = ({ children }) => {
  const action = getFirebaseAction();
  const expectedMode = action?.mode === "verifyAndChangeEmail"
    ? EMAIL_CHANGE_MODES.VERIFY_AND_CHANGE
    : EMAIL_CHANGE_MODES.LINK;
  const pending = action ? getPendingEmailChange({ mode: expectedMode }) : null;
  const shouldHandle = Boolean(action && (
    action.mode === "verifyAndChangeEmail" ||
    action.intent === EMAIL_CHANGE_LINK_INTENT ||
    pending
  ));
  const started = useRef(false);
  const [processing, setProcessing] = useState(shouldHandle);
  const auth = useContext(AuthContext);
  const inlineSignInPending = Boolean(auth.inlineSignInPending);
  const { axiosGraphQL } = useGraphql();

  useEffect(() => {
    if (!shouldHandle || started.current || inlineSignInPending) return;
    started.current = true;
    let unsubscribe = () => {};
    unsubscribe = firebase.auth().onAuthStateChanged(async (firebaseUser) => {
      if (auth.isInlineSignInPending?.()) return;
      if (!firebaseUser) {
        setProcessing(false);
        toast.info("Sign in with the account that requested this email change to continue.");
        return;
      }
      unsubscribe();
      setProcessing(true);
      try {
        // Existing-email changes are authoritatively bound to the signed-in
        // Core account and Firebase UID by the confirmation endpoint. Keep the
        // browser UID guard only for first-email credential linking.
        if (
          action.mode !== "verifyAndChangeEmail" &&
          pending &&
          firebaseUser.uid !== pending.firebaseUid
        ) {
          clearPendingEmailChange();
          stripFirebaseActionParams();
          throw new Error("account_mismatch");
        }
        const result = await completeEmailChangeAction({
          ...action,
          actionUrl: window.location.href,
          axiosGraphQL,
          previousUser: auth.authState.user,
        });
        if (!result) throw new Error("account_mismatch");
        auth.setAuthState(result);
        stripFirebaseActionParams();
        toast.success("Email verified successfully.");
      } catch (error) {
        toast.error(error.message === "email_change_impersonation_forbidden"
            ? "Email changes are unavailable while impersonating a user."
            : error.message === "email_change_sync_pending"
              ? "Email verification succeeded, but account synchronization is still pending. Refresh this page to retry."
              : error.message === "account_mismatch"
                ? "Sign in with the correct account and request a new email verification link."
            : "This email verification link is invalid, expired, or could not be completed.");
      } finally {
        setProcessing(false);
      }
    });
    return () => {
      unsubscribe();
      started.current = false;
    };
  }, [shouldHandle, inlineSignInPending]);

  if (processing && !inlineSignInPending) {
    return <Loader fullPage message="Verifying your email…" />;
  }
  return children;
};

export default FirebaseActionLinkHandler;
